import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OpsHeading, OpsState, OpsTable, errorText } from './OperationsUI';
import { toast } from '@/components/ui/sonner';
import { RefreshCw, RotateCcw, Info } from 'lucide-react';
import api from '@/lib/api';

export const PlatformEmailTemplates = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const configured = rows[0]?.email_configured;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows((await api.get('/platform/email-templates')).data); }
    catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (t) => { setActive(t); setForm({ subject: t.subject, body: t.body }); };

  const insertVar = (v) => {
    const el = bodyRef.current;
    const token = `{${v}}`;
    if (!el) { setForm((f) => ({ ...f, body: f.body + token })); return; }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    setForm((f) => ({ ...f, body: f.body.slice(0, start) + token + f.body.slice(end) }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const save = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error('Asunto y cuerpo son obligatorios'); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/platform/email-templates/${active.key}`, { subject: form.subject, body: form.body });
      setRows((prev) => prev.map((r) => (r.key === data.key ? { ...r, ...data } : r)));
      setActive({ ...active, ...data });
      toast.success('Plantilla guardada');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/email-templates/${active.key}/reset`);
      setRows((prev) => prev.map((r) => (r.key === data.key ? { ...r, ...data } : r)));
      setActive({ ...active, ...data }); setForm({ subject: data.subject, body: data.body });
      toast.success('Plantilla restaurada al texto predeterminado');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <OpsHeading title="Plantillas de correo" description="Edita el asunto y el cuerpo de los correos automáticos (confirmación de pedido, cambios de estado, aviso a la tienda, soporte y recuperación de contraseña) sin tocar código.">
        <Button variant="outline" data-testid="templates-reload" onClick={load}><RefreshCw /> Actualizar</Button>
      </OpsHeading>
      {!loading && !error && (
        <Alert className="mb-5" data-testid="templates-config-banner">
          <Info />
          <AlertDescription>
            {configured
              ? 'El envío por correo está configurado. Los cambios que guardes se aplican a los próximos correos enviados.'
              : 'El envío por correo aún no está configurado; las plantillas se guardan y se usarán en cuanto se active el correo.'}
          </AlertDescription>
        </Alert>
      )}
      <OpsState loading={loading} error={error} reload={load}>
        <OpsTable
          id="platform-templates-table"
          rows={rows}
          columns={[
            { key: 'label', label: 'Plantilla', render: (r) => <span>{r.label}<br /><span className="text-xs text-muted-foreground">{r.description}</span></span> },
            { key: 'subject', label: 'Asunto', render: (r) => <span className="text-xs">{r.subject}</span> },
            { key: 'is_custom', label: 'Estado', render: (r) => <Badge variant={r.is_custom ? 'default' : 'outline'} data-testid={`template-state-${r.key}`}>{r.is_custom ? 'Personalizada' : 'Predeterminada'}</Badge> },
          ]}
          actions={(r) => <Button variant="outline" data-testid={`template-edit-${r.key}`} onClick={() => open(r)}>Editar</Button>}
        />
      </OpsState>
      <Dialog open={!!active} onOpenChange={(v) => { if (!v && !busy) setActive(null); }}>
        <DialogContent className="seller-dialog max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{active?.label}</DialogTitle>
            <DialogDescription>{active?.description}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="template-subject" className="text-xs">Asunto</Label>
                <Input id="template-subject" className="mt-1" value={form.subject} data-testid="template-subject" onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="template-body" className="text-xs">Cuerpo (HTML)</Label>
                <Textarea id="template-body" ref={bodyRef} rows={10} className="mt-1 font-mono text-xs" value={form.body} data-testid="template-body" onChange={(e) => setForm({ ...form, body: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Variables disponibles</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="template-variables">
                  {(active.variables || []).map((v) => (
                    <button key={v} type="button" data-testid={`template-var-${v}`} onClick={() => insertVar(v)} className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-mono hover:border-brand-orange hover:text-brand-orange transition-colors">{`{${v}}`}</button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Haz clic en una variable para insertarla. Las variables desconocidas se dejan tal cual y no rompen el envío. El correo se enmarca automáticamente con la cabecera y el pie de Bodegón Mi Barrio.</p>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
                <Button variant="ghost" disabled={busy || !active.is_custom} data-testid="template-reset" onClick={reset}><RotateCcw className="h-4 w-4" /> Restaurar predeterminada</Button>
                <Button className="seller-primary" disabled={busy} data-testid="template-save" onClick={save}>{busy ? 'Guardando…' : 'Guardar plantilla'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlatformEmailTemplates;
